import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, Mail } from 'lucide-react';
import { anonymousAuditService } from '../services';
import Questionnaire from '../components/Questionnaire';
import DinSpecIntro from '../components/DinSpecIntro';
import AnonymousResult from '../components/AnonymousResult';
import TurnstileWidget, { isTurnstileEnabled } from '../components/TurnstileWidget';
import { Dialog, Button, Logo, Badge } from '../components/ds';
import { useIsMobile } from '../hooks/useMediaQuery';
import { trackEvent, ANON_CHECK_EVENTS } from '../utils/analytics';
import { PRIVACY_URL } from '../config';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

// Wortlaut identisch zur Single Source of Truth im Backend
// (backend/src/config/disclaimers.js → SELF_ASSESSMENT_DISCLAIMER). Für anonyme
// Nutzer gibt es keinen serverseitigen Consent-Log (kein Clerk-Konto); der Hinweis
// wird hier vor dem Start angezeigt und aktiv bestätigt.
const SELF_ASSESSMENT_DISCLAIMER_TEXT =
  'Ich habe verstanden, dass es sich bei Audicio um eine reine Self-Assessment-Plattform ' +
  'handelt. Die Ergebnisse basieren ausschließlich auf meinen eigenen Angaben, stellen kein ' +
  'Audit, keine Zertifizierung und keine rechtsverbindliche Sicherheitsberatung dar und ' +
  'ersetzen keine professionelle Prüfung.';

// Schlanke, login-freie Seiten-Hülle (die App-`Layout`-Komponente setzt Clerk-Login
// voraus und ist hier nicht verwendbar). `standard` = Normbezeichnung aus dem
// Katalog-Endpunkt; wird als Chip rechts im Header ausgewiesen (AUD-62).
const Shell = ({ standard, children }) => (
  <div style={{ minHeight: '100vh', background: 'var(--surface-page)', fontFamily: FONT }}>
    <header style={{
      height: 60, borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-card)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, padding: '0 20px',
    }}>
      <Logo height={28} />
      {standard && <Badge tone="neutral">{standard}</Badge>}
    </header>
    <main style={{ padding: '28px 16px 56px' }}>{children}</main>
  </div>
);

const Centered = ({ children }) => (
  <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-faint)', fontSize: 14, fontFamily: FONT }}>
    {children}
  </div>
);

const AnonymousCheckPage = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // phase: 'loading' | 'intro' | 'consent' | 'questionnaire' | 'result' | 'error'
  const [phase, setPhase] = useState('loading');
  const [posture, setPosture] = useState(null);
  // Kennzahlen des Fragenkatalogs für die Intro-Seite (AUD-62). Bewusst entkoppelt
  // vom Resume-Check: bleibt der Katalog aus, ist der Check trotzdem startbar — die
  // Intro rendert dann ohne Kennzahlen statt die Seite zu blockieren.
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  // Fortsetzen-Link (AUD-63): Dialog-Zustand und Rückmeldung eines eingelösten Links.
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeEmail, setResumeEmail] = useState('');
  const [resumeState, setResumeState] = useState('idle'); // idle | sending | sent | error
  const [resumeError, setResumeError] = useState('');
  const [resumeNotice, setResumeNotice] = useState('');
  const [resumeTurnstileToken, setResumeTurnstileToken] = useState('');
  // „Scharf", solange ein laufender Fragebogen ohne Abschluss offen ist — verlässt
  // der Nutzer dann die Seite, feuern wir ein Best-Effort-„Abandoned"-Event.
  const abandonArmedRef = useRef(false);

  // Resume: existiert ein gültiger Cookie, setzen wir beim letzten Stand fort.
  // Davor (AUD-63): Trägt die URL einen Fortsetzen-Token aus einer E-Mail, lösen
  // wir ihn zuerst ein — er setzt das Cookie, danach greift dieselbe Logik wie
  // bei einem normalen Wiederkommen. So gibt es nur einen Pfad, der über die
  // Phase entscheidet.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const resumeToken = params.get('resume');
      if (resumeToken) {
        try {
          await anonymousAuditService.redeemResumeLink(resumeToken);
        } catch (err) {
          if (!cancelled) {
            setResumeNotice(err?.response?.data?.error
              || 'Dieser Link ist ungültig oder abgelaufen. Sie können den Check hier neu starten.');
          }
        } finally {
          // Bearer-Credential nicht in Adresszeile und Browser-History stehen lassen.
          window.history.replaceState({}, '', window.location.pathname);
        }
      }

      try {
        const res = await anonymousAuditService.getCurrent();
        if (cancelled) return;
        const a = res.data.assessment;
        if (a.status === 'completed') {
          setPosture(a.frozenPosture);
          setPhase('result');
        } else {
          // Resume eines noch offenen Checks → Abbruch-Tracking wieder scharf stellen.
          abandonArmedRef.current = true;
          setPhase('questionnaire');
        }
      } catch (err) {
        if (cancelled) return;
        // 401 = kein/abgelaufener Cookie → normaler Erstkontakt, Intro zeigen.
        if (err?.response?.status === 401) {
          setPhase('intro');
        } else {
          setError('Der Check konnte nicht geladen werden. Bitte laden Sie die Seite neu.');
          setPhase('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    anonymousAuditService.getCatalog()
      .then((res) => { if (!cancelled) setCatalog(res.data); })
      .catch(() => { /* Intro rendert ohne Kennzahlen weiter */ });
    return () => { cancelled = true; };
  }, []);

  // Abbruch-Tracking: verlässt der Nutzer die Seite, während ein Check offen ist
  // (nicht abgeschlossen), feuern wir ein Best-Effort-„Abandoned"-Event. `pagehide`
  // greift zuverlässiger als `beforeunload` (auch mobil / bfcache). Bewusst nur
  // näherungsweise: ein Reload feuert hier ebenfalls, der Nutzer resumt danach aber
  // wieder (dann wird erneut scharf gestellt). Die belastbare Abbruchquote ergibt
  // sich ohnehin aus dem Funnel Started − Completed.
  useEffect(() => {
    const onLeave = () => {
      if (!abandonArmedRef.current) return;
      abandonArmedRef.current = false; // nur einmal pro offenem Check
      trackEvent(ANON_CHECK_EVENTS.abandoned);
    };
    window.addEventListener('pagehide', onLeave);
    return () => window.removeEventListener('pagehide', onLeave);
  }, []);

  const handleStart = async () => {
    if (starting) return;
    if (!consentChecked) return;
    if (isTurnstileEnabled() && !turnstileToken) return;
    setStarting(true);
    setError('');
    try {
      await anonymousAuditService.start({
        _hp: '',
        'cf-turnstile-response': turnstileToken || undefined,
      });
      trackEvent(ANON_CHECK_EVENTS.started);
      abandonArmedRef.current = true;
      setPhase('questionnaire');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Der Check konnte nicht gestartet werden. Bitte versuchen Sie es erneut.';
      setError(msg);
      setStarting(false);
    }
  };

  const handleComplete = useCallback(async () => {
    // Erfolgreich abgeschlossen → kein Abbruch mehr, Completed-Event feuern.
    abandonArmedRef.current = false;
    trackEvent(ANON_CHECK_EVENTS.completed);
    try {
      const res = await anonymousAuditService.getResult();
      setPosture(res.data);
      setPhase('result');
    } catch {
      setError('Das Ergebnis konnte nicht geladen werden. Bitte laden Sie die Seite neu.');
      setPhase('error');
    }
  }, []);

  // Datenzugriff für die Fragebogen-UI über die öffentlichen Endpunkte.
  const anonApi = useMemo(() => ({
    getQuestions: () => anonymousAuditService.getQuestions().then((r) => r.data),
    getAnswers: () => anonymousAuditService.getCurrent().then((r) => r.data.answers),
    saveAnswer: ({ questionId, answer }) => anonymousAuditService.saveAnswer({ questionId, answer }),
    complete: () => anonymousAuditService.complete(),
  }), []);

  const handleTurnstileToken = useCallback((token) => setTurnstileToken(token), []);
  const handleResumeTurnstileToken = useCallback((token) => setResumeTurnstileToken(token), []);

  // Fortsetzen-Link anfordern (AUD-63).
  const handleRequestResumeLink = async () => {
    if (resumeState === 'sending') return;
    setResumeState('sending');
    setResumeError('');
    try {
      await anonymousAuditService.requestResumeLink({
        email: resumeEmail.trim(),
        _hp: '',
        'cf-turnstile-response': resumeTurnstileToken || undefined,
      });
      setResumeState('sent');
    } catch (err) {
      setResumeError(err?.response?.data?.error || 'Der Link konnte nicht verschickt werden. Bitte versuchen Sie es erneut.');
      setResumeState('error');
    }
  };

  const closeResumeDialog = () => {
    setResumeOpen(false);
    // Nach dem Schließen zurück auf Anfang, damit ein erneutes Öffnen nicht die
    // alte Erfolgs-/Fehlermeldung zeigt. Der Turnstile-Token muss mit zurück:
    // er ist einmalig verwendbar, und der Dialog unmountet beim Schließen — beim
    // erneuten Öffnen holt das frisch gemountete Widget einen neuen.
    setResumeState('idle');
    setResumeError('');
    setResumeTurnstileToken('');
  };

  if (phase === 'loading') {
    return <Shell standard={catalog?.standard}><Centered>Lädt…</Centered></Shell>;
  }

  if (phase === 'error') {
    return (
      <Shell standard={catalog?.standard}>
        <div style={{
          maxWidth: 560, margin: '0 auto',
          background: 'var(--crit-50)', border: '1px solid var(--crit-100)', borderRadius: 'var(--radius-lg)',
          padding: '16px 20px', color: 'var(--crit-700)', fontSize: 14,
        }}>{error || 'Es ist ein Fehler aufgetreten.'}</div>
      </Shell>
    );
  }

  if (phase === 'result') {
    return (
      <Shell standard={catalog?.standard}>
        <AnonymousResult posture={posture} onRegister={() => navigate('/sign-up')} />
      </Shell>
    );
  }

  if (phase === 'questionnaire') {
    return (
      <Shell standard={catalog?.standard}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          {/* Pausieren sichtbar machen (AUD-63): Der Check dauert rund eine Stunde,
              und ohne diesen Hinweis weiß niemand, dass Schließen gefahrlos ist. */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap', marginBottom: 14,
          }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Ihre Antworten werden automatisch gespeichert. Sie können jederzeit schließen und
              in diesem Browser weitermachen.
            </span>
            <Button variant="secondary" size="sm" onClick={() => setResumeOpen(true)}
              leadingIcon={<Mail width={15} height={15} strokeWidth={1.75} />}>
              Später fortsetzen
            </Button>
          </div>

          <Questionnaire templateId="din_spec_27076" api={anonApi} enableAi={false} onComplete={handleComplete} />
        </div>

        <Dialog
          open={resumeOpen}
          onClose={closeResumeDialog}
          title="Später fortsetzen"
          description="Wir schicken Ihnen einen Link, mit dem Sie den Check fortsetzen können — auch auf einem anderen Gerät."
          width={520}
          footer={resumeState === 'sent' ? (
            <Button onClick={closeResumeDialog}>Schließen</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={closeResumeDialog} disabled={resumeState === 'sending'}>Abbrechen</Button>
              <Button
                onClick={handleRequestResumeLink}
                disabled={resumeState === 'sending' || !resumeEmail.trim() || (isTurnstileEnabled() && !resumeTurnstileToken)}
              >
                {resumeState === 'sending' ? 'Wird gesendet …' : 'Link schicken'}
              </Button>
            </>
          )}
        >
          {resumeState === 'sent' ? (
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-body)', lineHeight: 1.6 }}>
              Der Link ist unterwegs an <strong>{resumeEmail.trim()}</strong>. Schauen Sie gegebenenfalls
              im Spam-Ordner nach. Sie können dieses Fenster jetzt schließen — Ihr Fortschritt bleibt erhalten.
            </p>
          ) : (
            <>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', marginBottom: 6 }}>
                E-Mail-Adresse
                <input
                  type="email"
                  value={resumeEmail}
                  onChange={(e) => setResumeEmail(e.target.value)}
                  placeholder="name@unternehmen.de"
                  autoComplete="email"
                  style={{ marginTop: 6, fontWeight: 400 }}
                />
              </label>

              {isTurnstileEnabled() && (
                <div style={{ marginTop: 14 }}>
                  <TurnstileWidget onToken={handleResumeTurnstileToken} />
                </div>
              )}

              <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Der Link ist 7 Tage gültig. Wer ihn öffnet, sieht Ihre bisherigen Antworten — bitte nicht
                weiterleiten. Wir speichern Ihre Adresse zu diesem Check; sie wird spätestens 14 Tage
                nach dem Start zusammen mit dem Check gelöscht. Mehr dazu in der{' '}
                <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-link)' }}>
                  Datenschutzerklärung
                </a>.
              </p>

              {resumeError && (
                <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--crit-600)' }}>{resumeError}</p>
              )}
            </>
          )}
        </Dialog>
      </Shell>
    );
  }

  // phase === 'intro' → Info + Consent + optional Turnstile, dann Start.
  return (
    <Shell standard={catalog?.standard}>
      {resumeNotice && (
        <div style={{
          maxWidth: 1120, margin: '0 auto 20px',
          background: 'var(--warn-50)', border: '1px solid var(--warn-100)',
          borderRadius: 'var(--radius-lg)', padding: '14px 18px',
          color: 'var(--warn-700)', fontSize: 13, lineHeight: 1.6,
        }}>{resumeNotice}</div>
      )}

      <DinSpecIntro catalog={catalog} onStart={() => setPhase('consent')} />

      <Dialog
        open={phase === 'consent'}
        onClose={() => { setPhase('intro'); setError(''); }}
        width={560}
      >
        <div style={{ padding: isMobile ? '4px 2px 6px' : '6px 6px 8px' }}>
          <div style={{
            width: 52, height: 52, borderRadius: 13,
            background: 'var(--warn-50, #FDF6EC)', color: 'var(--warn-700, #A15C07)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18,
          }}>
            <ShieldAlert width={26} height={26} />
          </div>
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#101623' }}>
            Bevor Sie starten
          </h2>
          <p style={{ color: '#667085', fontSize: 14, margin: '0 0 18px', lineHeight: 1.6 }}>
            Bitte lesen und bestätigen Sie den folgenden Hinweis.
          </p>

          <div style={{
            background: '#F7F8FA', border: '1px solid #E7E9EF', borderRadius: 10,
            padding: '16px 18px', color: '#3A4252', fontSize: 14, lineHeight: 1.6, marginBottom: 18,
          }}>
            {SELF_ASSESSMENT_DISCLAIMER_TEXT}
          </div>

          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
            fontSize: 14, color: '#101623', lineHeight: 1.5,
          }}>
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
            />
            <span>Ich habe den Hinweis gelesen und bestätige ihn.</span>
          </label>

          {isTurnstileEnabled() && (
            <div style={{ marginTop: 18 }}>
              <TurnstileWidget onToken={handleTurnstileToken} />
            </div>
          )}

          {error && (
            <p style={{ color: 'var(--crit-600, #C0342B)', fontSize: 13, margin: '14px 0 0' }}>{error}</p>
          )}

          <div style={{ marginTop: 24 }}>
            <Button
              size="md"
              disabled={!consentChecked || starting || (isTurnstileEnabled() && !turnstileToken)}
              onClick={handleStart}
            >
              {starting ? 'Wird gestartet …' : 'Check jetzt starten →'}
            </Button>
          </div>
        </div>
      </Dialog>
    </Shell>
  );
};

export default AnonymousCheckPage;
